import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useToggle } from "react-use";
import { Button, Label, LayerContextProvider, layers } from "@activecollab/components";
import classNames from "classnames";
import moment from "moment";
import PropTypes from "prop-types";
import { addReminder } from "~/store/actions/actions.reminders.jsx";
import { getUserTimezone } from "~/store/selectors/selector.configOptions.jsx";
import { getSubscriberIdsByTypeAndId } from "~/store/selectors/selector.subscription.jsx";
import { getLoggedUserId, getUsersEmailIdMap } from "~/store/selectors/selector.users.jsx";
import { sortCompare } from "~/utils/utils.jsx";
import { QuickReminderMenu } from "./QuickReminderMenu.js";
import Reminder from "./Reminder.jsx";
import ReminderFormContainer from "./ReminderFormContainer.js";
import { REMINDERS_SUPPORTED_PARENT_TYPES } from "./utils.js";
import { getClosestOccurrence, isAllDay } from "../Calendar/calendarUtils.js";

const Reminders = ({ parent, members, onDelete, reminders }) => {
  const [showModal, setShowModal] = useState(false);
  const [reminderId, setReminderId] = useState(0);
  const [showMore, toggle] = useToggle(false);
  const [recipients, setRecipients] = useState(null); //for quick actions preselect
  const loggedUserId = useSelector(getLoggedUserId);
  const queryClient = useQueryClient();
  const timezone = useSelector(getUserTimezone);
  const dispatch = useDispatch();

  const subscriberIds = useSelector((state) => getSubscriberIdsByTypeAndId(state, parent.class, parent.id));
  const usersEmailIdMap = useSelector(getUsersEmailIdMap);

  const allReminders = useMemo(() => {
    let sortedRemindersToShow = [...reminders].sort((a, b) =>
      sortCompare(a.remind_on ?? a.send_on, b.remind_on ?? b.send_on)
    );
    if (!showMore && reminders?.length > 3) sortedRemindersToShow.length = 3;
    return sortedRemindersToShow;
  }, [reminders, showMore]);

  const { t } = useTranslation();

  const handleShowModal = useCallback(() => {
    setShowModal(true);
  }, [setShowModal]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setRecipients(null);
  }, [setShowModal]);

  const handleOnEdit = useCallback(
    (id) => {
      handleShowModal();
      setReminderId(id);
    },
    [handleShowModal]
  );

  const handleOnAdd = useCallback(() => {
    handleShowModal();
    setReminderId(0);
  }, [handleShowModal]);

  const menuOptions = useMemo(() => {
    if (REMINDERS_SUPPORTED_PARENT_TYPES.includes(parent?.class)) {
      const options = [{ id: "me", name: t("Me"), subscribers: [loggedUserId] }];

      if (parent.class === "Task" && parent.assignee_id) {
        options.push({ id: "assignee", name: t("Assignee"), subscribers: [parent.assignee_id] });
      }

      if (parent.class === "CalendarEvent" && parent?.participants && parent.participants.length > 0) {
        const participantsIds = parent.participants.map((email) => usersEmailIdMap[`${email}`]);

        options.push({ id: "participants", name: t("Participants"), subscribers: participantsIds });
      }

      if (subscriberIds.length) {
        options.push({ id: "subscribers", name: t("Subscribers"), subscribers: subscriberIds });
      }

      options.push({ id: "custom", name: `${t("Custom")}...`, subscribers: [], onClick: handleOnAdd });

      return options;
    }
    return [];
  }, [handleOnAdd, loggedUserId, parent, subscriberIds, t, usersEmailIdMap]);

  const additionalSubmenuOptions = useMemo(() => {
    if (REMINDERS_SUPPORTED_PARENT_TYPES.includes(parent?.class)) {
      const options = [];

      if (parent.class === "CalendarEvent" && !isAllDay(parent)) {
        [0, 5, 15, 30, 60].forEach((minutes) => {
          const date = moment(getClosestOccurrence(parent, timezone)).add(-1 * minutes, "minutes");
          if (date.isAfter(moment().add(1, "minute"))) {
            let name = "";
            if (minutes === 0) {
              name = t("At time of event");
            } else {
              if (minutes === 60) {
                name = `1 ${t("hour before")}`;
              } else {
                name = t("{{ minutes }} minutes before", { minutes: minutes });
              }
            }
            options.push({
              id: `${minutes}-minutes-before`,
              name: name,
              time_label: null,
              remind_on: null,
              is_relative: true,
              relative_value: minutes === 60 ? -1 : minutes * -1,
              relative_unit: minutes === 60 ? "hour" : "minute",
            });
          }
        });
      }

      return options;
    }
    return [];
  }, [parent, t, timezone]);

  const onSubmit = useCallback(
    (option) => {
      const { remind_on, subscribers, id, ...rest } = option;
      if (id === "custom") {
        setRecipients(subscribers);
        setShowModal(true);
      } else {
        dispatch(
          addReminder(parent.id, parent.class, {
            remind_on: remind_on ? moment.unix(remind_on).utc().format() : undefined,
            subscribers,
            ...rest,
          })
        ).then((result) => {
          if (result?.error) {
            return;
          }
          queryClient.invalidateQueries(["notifications", "for-screen"]);
          handleCloseModal();
        });
      }
    },
    [dispatch, parent.id, parent.class, queryClient, handleCloseModal]
  );

  return (
    <div
      className={classNames("tw-flex tw-flex-col tw-items-start tw-gap-2 tw-pb-0", {
        "lg:tw-pb-[48px]": allReminders && allReminders.length === 0,
        "lg:tw-pb-5": allReminders && allReminders.length > 0,
      })}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
        <Label size="small">{t("Reminders")}</Label>
        <QuickReminderMenu
          options={menuOptions}
          customEnabled={true}
          additionalSubmenuOptions={additionalSubmenuOptions}
          onSubmit={onSubmit}
        />
      </div>
      {allReminders && allReminders.length
        ? allReminders.map((reminder) => (
            <Reminder
              key={reminder.id}
              onDelete={onDelete}
              reminder={reminder}
              onEdit={handleOnEdit}
            />
          ))
        : null}
      {showModal && (
        <LayerContextProvider value={{ zIndex: layers.skyscraper }}>
          <ReminderFormContainer
            id={reminderId}
            parent={parent}
            members={members}
            recipients={recipients}
            onCancel={handleCloseModal}
            onSubmit={handleCloseModal}
            open={showModal}
          />
        </LayerContextProvider>
      )}
      {reminders?.length > 3 ? (
        <div className="tw-mt-2">
          <Button
            variant="tertiary"
            size="small"
            onClick={toggle}
            className="-tw-ml-2"
          >
            {showMore ? t("Show less") : `${t("Show")} ${reminders?.length - 3} ${t("more")}`}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

Reminders.propTypes = {
  parent: PropTypes.object.isRequired,
  members: PropTypes.array.isRequired,
  onDelete: PropTypes.func,
  reminders: PropTypes.array,
};

Reminders.defaultProps = {
  onDelete: () => null,
  reminders: [],
};

export default React.memo(Reminders);
